import React from 'react';
import { Map, Navigation, AlertTriangle, Clock, Repeat, LayoutGrid } from 'lucide-react';
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, AreaChart, Area } from 'recharts';

interface NavigationEvent {
  timestamp: Date;
  x: number;
  y: number;
  duration: number;
  type: 'normal' | 'hesitation' | 'repetitive' | 'wandering';
  location: string;
}

interface HeatmapPoint {
  x: number;
  y: number;
  value: number;
}

interface NavigationMetrics {
  repetitivePatterns: number;
  hesitationPoints: number;
  averagePathLength: number;
  spatialConfusionScore: number;
  commonPaths: {
    start: string;
    end: string;
    frequency: number;
    efficiency: number;
  }[];
  timeInLocations: {
    location: string;
    duration: number;
    visits: number;
  }[];
  hourlyActivity: {
    hour: number;
    navigationEvents: number;
    confusionEvents: number;
  }[];
}

// Mock data generation
const generateNavigationData = (residentId: string): {events: NavigationEvent[], metrics: NavigationMetrics} => {
  const locations = ['Bedroom', 'Bathroom', 'Common Area', 'Dining Room', 'Activity Room', 'Garden'];
  const events: NavigationEvent[] = [];
  const hourlyActivity = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    navigationEvents: Math.floor(Math.random() * 10),
    confusionEvents: Math.floor(Math.random() * 3)
  }));

  // Generate navigation events
  for (let i = 0; i < 100; i++) {
    events.push({
      timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000),
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 300,
      type: ['normal', 'hesitation', 'repetitive', 'wandering'][Math.floor(Math.random() * 4)] as any,
      location: locations[Math.floor(Math.random() * locations.length)]
    });
  }

  const metrics: NavigationMetrics = {
    repetitivePatterns: Math.floor(Math.random() * 10),
    hesitationPoints: Math.floor(Math.random() * 15),
    averagePathLength: 50 + Math.random() * 30,
    spatialConfusionScore: Math.random() * 100,
    commonPaths: locations.slice(0, 3).map(loc => ({
      start: loc,
      end: locations[Math.floor(Math.random() * locations.length)],
      frequency: Math.floor(Math.random() * 50),
      efficiency: Math.random() * 100
    })),
    timeInLocations: locations.map(loc => ({
      location: loc,
      duration: Math.random() * 240,
      visits: Math.floor(Math.random() * 20)
    })),
    hourlyActivity
  };

  return { events, metrics };
};

interface Props {
  residentId: string;
  residentName: string;
}

const SpatialNavigationAnalytics: React.FC<Props> = ({ residentId, residentName }) => {
  const { events, metrics } = generateNavigationData(residentId);

  const getEventColor = (type: string) => {
    switch (type) {
      case 'hesitation': return '#fbbf24';
      case 'repetitive': return '#f87171';
      case 'wandering': return '#ef4444';
      default: return '#22c55e';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <Map className="text-purple-600" />
            Spatial Navigation Analysis
          </h3>
          <p className="text-gray-600">Tracking spatial awareness and navigation patterns</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 text-yellow-800 rounded-full">
          <AlertTriangle size={16} />
          {metrics.spatialConfusionScore > 70 ? 'High' : metrics.spatialConfusionScore > 40 ? 'Moderate' : 'Low'} Confusion Risk
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Movement Heatmap */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-4">Movement Patterns</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="x" name="X Position" unit="m" />
                <YAxis type="number" dataKey="y" name="Y Position" unit="m" />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    `${value}${name.includes('Position') ? 'm' : 's'}`,
                    name
                  ]}
                  labelFormatter={(label) => `Location: ${events[label as number]?.location}`}
                />
                <Legend />
                {['normal', 'hesitation', 'repetitive', 'wandering'].map(type => (
                  <Scatter
                    key={type}
                    name={type.charAt(0).toUpperCase() + type.slice(1)}
                    data={events.filter(e => e.type === type)}
                    fill={getEventColor(type)}
                  />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Hourly Activity Pattern */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-4">24-Hour Activity Pattern</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={metrics.hourlyActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="hour" 
                  tickFormatter={(hour) => `${hour}:00`}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    value,
                    name === 'navigationEvents' ? 'Navigation Events' : 'Confusion Events'
                  ]}
                  labelFormatter={(hour) => `${hour}:00`}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="navigationEvents"
                  stackId="1"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.3}
                  name="Navigation Events"
                />
                <Area
                  type="monotone"
                  dataKey="confusionEvents"
                  stackId="2"
                  stroke="#ef4444"
                  fill="#ef4444"
                  fillOpacity={0.3}
                  name="Confusion Events"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-4">Navigation Metrics</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-3">
              <div className="text-sm text-gray-600">Repetitive Patterns</div>
              <div className="text-xl font-semibold text-purple-600">
                {metrics.repetitivePatterns}
              </div>
              <div className="text-xs text-gray-500">Last 24 hours</div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <div className="text-sm text-gray-600">Hesitation Points</div>
              <div className="text-xl font-semibold text-purple-600">
                {metrics.hesitationPoints}
              </div>
              <div className="text-xs text-gray-500">Last 24 hours</div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <div className="text-sm text-gray-600">Avg Path Length</div>
              <div className="text-xl font-semibold text-purple-600">
                {metrics.averagePathLength.toFixed(1)}m
              </div>
              <div className="text-xs text-gray-500">Per journey</div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <div className="text-sm text-gray-600">Confusion Score</div>
              <div className="text-xl font-semibold text-purple-600">
                {metrics.spatialConfusionScore.toFixed(1)}
              </div>
              <div className="text-xs text-gray-500">Out of 100</div>
            </div>
          </div>
        </div>

        {/* Common Paths */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-4">Common Paths</h4>
          <div className="space-y-3">
            {metrics.commonPaths.map((path, index) => (
              <div key={index} className="bg-white rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">
                      {path.start} → {path.end}
                    </div>
                    <div className="text-sm text-gray-600">
                      {path.frequency} times today
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs ${
                    path.efficiency > 70 ? 'bg-green-100 text-green-800' :
                    path.efficiency > 40 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {path.efficiency.toFixed(0)}% efficient
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Location Time Analysis */}
      <div className="mt-6">
        <h4 className="font-semibold mb-4">Time in Locations</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {metrics.timeInLocations.map((loc, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm font-medium">{loc.location}</div>
              <div className="text-2xl font-semibold text-purple-600">
                {Math.floor(loc.duration / 60)}h {Math.floor(loc.duration % 60)}m
              </div>
              <div className="text-xs text-gray-600">{loc.visits} visits</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpatialNavigationAnalytics; 